/**
 * pages/ImportKontakWizard.js
 * Contacts xlsx import — the full 8-step wizard, ALL batches now done: Pilih File -> Nama Toko
 * Ganda -> Alamat Tambahan -> Nilai Kosong -> Sales Tambahan -> Pemetaan Sales -> Kelompok Bisnis
 * -> Ringkasan & Impor. Step 7 (firm grouping / business linking, batch 5c) proposes which
 * resolved shops look like the same real firm (`utils/contactImportGroup.js`'s own
 * `groupProposedFirms`, batch 3b) and lets a human confirm/reject/edit each proposal; a confirmed
 * group becomes a real `Business`, linked to the real `Contact`s this run actually created, via
 * `utils/contactImportApplyGroups.js` (batch 5c's own new pure module) at commit time. Step 5
 * (Sales Tambahan, a gap fix found during the batch-5c documentation audit) resolves
 * `contactImportParser.js`'s own `possible-second-sales` problem — a continuation row whose Sales
 * disagrees with its parent's — which used to be raised and read by NOTHING downstream, silently
 * dropping a real second salesperson (confirmed in the client's own file: CABANG row 225). Placed
 * BEFORE Pemetaan Sales deliberately, since an "add" decision here can introduce a brand-new Sales
 * name that then needs its own step-6 mapping.
 *
 * All state is local to this component — closing (or a completed commit) discards it, same
 * "genuinely one-time, not resumable" contract `ImportWizard.js` (the unrelated catalog import)
 * already states for itself.
 *
 * The pure engine underneath: `utils/contactImportRead.js` (the raw-error-cell reading fix),
 * `utils/contactImportParser.js` (batch 1), `utils/contactImportResolve.js` (batch 3a — now also
 * where the "Sales Tambahan" decision itself lives, its own 6th resolved ambiguity),
 * `utils/contactImportGroup.js` (batch 3b), `utils/contactImportSalesMap.js` (batch 4),
 * `utils/contactImportCommit.js` (batch 2), `utils/contactImportApplyGroups.js` (batch 5c) — this
 * file is wiring, tables, the confirm-before-losing-work gates, and the manual-testing fixes
 * documented inline below. The commit chain is exactly `resolveContactImport` ->
 * `resolveSalesPersonMapping` -> `commitContactImport` -> `applyConfirmedGroups` -> ONE `onCommit`
 * call, matching `contactImportSalesMap.js`'s own documented composition; nothing is written to
 * the real app `data` until that single call (`App.js`'s own `commitKontakImport`, which does the
 * one `update()`).
 *
 * Manual-testing pass (real 634-contact import): a pinned footer (Kembali/Lanjut, plus what's
 * still unanswered on the current step, each item jumping to its own row); missing address
 * gained the same explicit "leave it empty" affordance city already had; unusable Sales moved
 * from step 4 into what's now step 6, resolved directly against PEOPLE (not a name first) — see
 * that section's own comment for the data-contract note; and a provinces-as-cities finding
 * (reported only, not built — see this file's own CLAUDE.md entry).
 */
import React, { useEffect, useMemo, useState } from "react";
import { read, utils as xlsxUtils } from "xlsx";
import { patchSalesErrorCells } from "../utils/contactImportRead";
import { parseContactWorkbook } from "../utils/contactImportParser";
import {
  resolveContactImport,
  buildEffectiveShops,
  collectDecisionsNeeded,
  shopKey,
  normalizeDuplicateDecision,
  normalizeAddressDecision,
  normalizeMissingAddressDecision,
  normalizeCityDecision,
  normalizeSecondSalesDecision,
} from "../utils/contactImportResolve";
import { nameCollides } from "../utils/contacts";
import {
  salesNameKey,
  collectSalesNamesNeeded,
  resolveSalesPersonMapping,
  normalizeSalesMapDecision,
} from "../utils/contactImportSalesMap";
import { commitContactImport } from "../utils/contactImportCommit";
import { groupProposedFirms } from "../utils/contactImportGroup";
import { applyConfirmedGroups } from "../utils/contactImportApplyGroups";
import ConfirmModal from "../components/ConfirmModal";

const TOTAL_STEPS = 8;
const STEP_TITLES = [
  "Pilih File", "Nama Toko Ganda", "Alamat Tambahan", "Nilai Kosong", "Sales Tambahan",
  "Pemetaan Sales", "Kelompok Bisnis", "Ringkasan & Impor",
];

/** Trivial local trim/whitespace/case normalizer for name comparison — this app already carries
 *  several near-identical private `norm(s)` helpers across contacts.js/persons.js/branches.js/
 *  contactImportParser.js/contactImportResolve.js; this is the same one-liner, kept local for
 *  the same reason those are (see contactImportParser.js's own comment on this precedent). */
function normName(s) {
  return String(s == null ? "" : s).trim().replace(/\s+/g, " ").toUpperCase();
}

/** Manual-testing fix: the row comparison in step 2 must let a human actually judge "one shop or
 *  two" — two rows can share the exact same address and still be genuinely different shops (a
 *  real case in the file: INDAH MOTOR, same street twice, two different salespeople). One entry
 *  per field shown side by side; whichever fields disagree across the group get highlighted. */
const COMPARISON_FIELDS = [
  { key: "address", label: "Alamat" },
  { key: "sales", label: "Sales" },
  { key: "ekspedisi", label: "Ekspedisi" },
  { key: "contactPerson", label: "Kontak" },
];
function buildComparisonRows(members) {
  return members.map((m) => ({
    row: m.row,
    address: m.addresses.join("; ") || "—",
    sales: m.sales || "—",
    ekspedisi: m.ekspedisi.join(", ") || "—",
    contactPerson: m.contactPerson.join(", ") || "—",
  }));
}
function differingComparisonFields(rows) {
  return new Set(
    COMPARISON_FIELDS.filter((f) => new Set(rows.map((r) => r[f.key])).size > 1).map((f) => f.key)
  );
}
const DIFFER_STYLE = { background: "#fef3c7" };

const CODE_FORMAT = /^[A-Z0-9]{1,4}$/;

/** The plan row (from `resolveContactImport`'s own `plan`) a `collectSalesNamesNeeded` shop
 *  reference points at — matched the same way `contactImportSalesMap.js`'s own restored
 *  `row`/`sourceRows` fields are derived, via `_meta` (list + anchorRow), never by array
 *  position. Used only for the step-5 "what does skipping this name cost" preview below. */
function planRowForShop(plan, shopRef) {
  return plan.find((r) => r.list === shopRef.list && r._meta && r._meta.anchorRow === shopRef.anchorRow);
}

/** How many of a Sales name's own shops have THIS name as their ONLY salesName — i.e. would
 *  definitely be excluded (no other salesperson to fall back on) if this name is skipped. A
 *  multi-name shop (the row-225 "two real salespeople" class) is deliberately NOT counted here
 *  even though skipping this name might still end up excluding it — that depends on the OTHER
 *  name's own, possibly-still-undecided, choice, which this preview has no way to know yet; this
 *  number is only ever the definite, unconditional cost. */
function skipCost(entry, plan) {
  return entry.shops.filter((s) => {
    const row = planRowForShop(plan, s);
    return row && Array.isArray(row.salesNames) && row.salesNames.length === 1;
  }).length;
}

/** Shared by both the live PREVIEW (step 6, before commit) and the frozen RESULT (after commit)
 *  — same builder, called at different times, so the two screens can never structurally disagree
 *  about what "not created" means. Every shop left out is named, with exactly one of four real
 *  reasons: skipped at step 4 — structurally UNREACHABLE since the manual-testing pass moved
 *  unusable-Sales resolution to step 5 (every unusable-Sales row now always reaches `plan` with
 *  a real `salesNames` entry, never a batch-3a-level skip) — kept as defense in depth, matching
 *  this codebase's own "never silently drop a row" discipline, in case a future change reopens a
 *  path to it; skipped at step 5 (the resolved Sales name's own person mapping was skipped —
 *  covers BOTH a regular name and one of the direct-person-pick shops); already existed
 *  (commitContactImport's own create-only skip); or failed validation (`validateContactFields`'s
 *  own text, shown VERBATIM, never reinterpreted — the task's own three named reasons don't
 *  include this fourth one, but "nothing that will be left out may be hidden" is the stronger,
 *  more general instruction, and a validation failure is knowable from the exact same pure
 *  computation before commit, not just after). */
function buildImportSummary(resolvedResult, salesResult, commitResult, existingPersons) {
  const existingIds = new Set((existingPersons || []).map((p) => p.id));
  const newPersons = (salesResult.data.persons || []).filter((p) => !existingIds.has(p.id));
  const notCreated = [];
  for (const e of resolvedResult.excluded || []) {
    notCreated.push({ list: e.sheet, name: e.name, reason: "Dilewati di Langkah 4 (Sales tidak bisa dipakai)." });
  }
  for (const e of salesResult.excluded || []) {
    notCreated.push({ list: e.sheet, name: e.name, reason: "Sales untuk toko ini dilewati di Langkah 5." });
  }
  for (const s of commitResult.report.skipped || []) {
    notCreated.push({ list: s.list, name: s.name, reason: "Sudah ada di aplikasi." });
  }
  for (const f of commitResult.report.failed || []) {
    notCreated.push({ list: f.list, name: f.name, reason: f.message });
  }
  return { report: commitResult.report, newPersons, notCreated };
}

/** Scrolls a footer-flagged row into view and flashes it briefly, so a click on "4 alamat kosong
 *  belum dijawab" actually lands somewhere concrete — never just a count with nothing to point
 *  at. Optional-chained the same way `BusinessLinkModal.js`'s own `scrollIntoView` call already
 *  is elsewhere in this app — jsdom doesn't implement it at all (confirmed by that file's own
 *  comment), so this is a real-browser-only effect, harmless (a no-op) under test. */
function jumpToRow(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView && el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("wizard-jump-highlight");
  setTimeout(() => el.classList.remove("wizard-jump-highlight"), 1600);
}

export default function ImportKontakWizard({ data, onCommit, onDone }) {
  const [step, setStep] = useState(1);

  const [parseError, setParseError] = useState("");
  const [parsed, setParsed] = useState(null);

  const [duplicateDecisions, setDuplicateDecisions] = useState({});
  const [addressDecisions, setAddressDecisions] = useState({});
  const [missingAddressDecisions, setMissingAddressDecisions] = useState({});
  const [cityDecisions, setCityDecisions] = useState({});
  // Step 5 ("Sales Tambahan" — the gap-fix batch that found `possible-second-sales` was raised
  // but read by nothing downstream) — one entry per CONTINUATION row (`shopKey`, same convention
  // as extraAddresses/missingAddress/city): `{action: "add"|"keep"}`. Lives in
  // `contactImportResolve.js`'s own `decisions.secondSales` — see that file's own header for why
  // this is the 6th ambiguity resolved there, not a step-5(old)/SalesMap concern.
  const [secondSalesDecisions, setSecondSalesDecisions] = useState({});
  // Step 6 (batch 5b) — a single FLAT map keyed by salesNameKey, mirroring
  // contactImportSalesMap.js's own decisions contract exactly (this batch resolves exactly one
  // kind of ambiguity, unlike batch 3a's five nested sub-maps). Regular Sales names only — the
  // unusable-Sales shops (manual-testing fix, below) have their own, separate state.
  const [salesMapDecisions, setSalesMapDecisions] = useState({});
  // Manual-testing fix — one entry per unusable-Sales ROW (keyed by shopKey, not a Sales-name
  // key): {mode:"existing", personId} | {mode:"same", nameKey} | {mode:"create", name, code} |
  // {mode:"skip"}. See "Step 6 — unusable Sales, resolved directly against people" below for the
  // full contract and why this stays separate from `salesMapDecisions`.
  const [unusablePersonChoices, setUnusablePersonChoices] = useState({});

  // Correction 1: a step-2 kind change that would orphan later-step answers asks first, and
  // does nothing at all until confirmed. Holds {message, apply} while a confirm is pending.
  const [pendingDuplicateChange, setPendingDuplicateChange] = useState(null);
  const [pendingClose, setPendingClose] = useState(false);
  // Step 5's own "SKIP must say what it costs before the user commits to it" gate (regular Sales
  // names only — a direct-person-pick row's own skip is unambiguous, see below) — holds
  // {message, onConfirm} while a skip-cost confirm is pending; a zero-cost skip never prompts.
  const [pendingSkipSales, setPendingSkipSales] = useState(null);

  // Step 7 — committing state (painted before the synchronous commit work, same
  // ImportWizard.js trick) and the FROZEN post-commit summary (null until a real commit runs;
  // once set, the screen shows the RESULT instead of the live preview).
  const [committing, setCommitting] = useState(false);
  const [finalSummary, setFinalSummary] = useState(null);

  // ── Step 7 (batch 5c): firm grouping — see that step's own comment block, below, for the
  // heavy-compute/invalidation contract. `groupingResult` is real STATE, never a `useMemo` —
  // `groupProposedFirms` costs ~5s on the real file, so it must run at most once per entry into
  // this step, deferred via `setTimeout(0)` the same way `runCommit` already defers its own
  // (much cheaper) synchronous work — never silently on every render the way every OTHER
  // derived value in this file is. `forPlan` records which `salesMapResult.plan` reference the
  // stored result was computed FROM; a mismatch (ANY earlier-step decision changed, which always
  // produces a brand-new `plan` array — pure functions all the way down) means it's stale.
  const [groupingResult, setGroupingResult] = useState(null); // {groups, unmatched, forPlan} | null
  const [groupComputing, setGroupComputing] = useState(false);
  // One entry per group's own INDEX in `groupingResult.groups` — positional and deliberately so;
  // see `contactImportApplyGroups.js`'s own header for why an emergent computed cluster has no
  // better natural key, and why this is safe ONLY paired with the SAME `groups` array (reset to
  // `{}` every time `groupingResult` is recomputed, below).
  const [groupDecisions, setGroupDecisions] = useState({});

  // ── Sales names already present in the file — dual purpose: the raw material for step 5's own
  // "same as another Sales name in this step" option (below), and previously step 4's own
  // now-removed unusable-Sales picker. Sourced from `parsed` directly (never from `resolved.plan`)
  // so it carries no circular dependency on the unusable-Sales resolution that itself needs it.
  const knownSalesNames = useMemo(() => {
    if (!parsed) return [];
    const byKey = new Map();
    for (const sheet of ["CABANG", "PUSAT"]) {
      const usage = (parsed.sheets[sheet] && parsed.sheets[sheet].stats && parsed.sheets[sheet].stats.salesUsage) || [];
      for (const u of usage) {
        const key = salesNameKey(u.name);
        if (!byKey.has(key)) byKey.set(key, { key, name: u.name, count: 0 });
        byKey.get(key).count += u.count;
      }
    }
    return [...byKey.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [parsed]);
  const regularNameTextByKey = useMemo(
    () => new Map(knownSalesNames.map((n) => [n.key, n.name])),
    [knownSalesNames]
  );

  // ── Manual-testing fix — unusable Sales moved to step 5, resolved directly against people ──
  //
  // DATA CONTRACT: batches 2/3a/4 (contactImportCommit.js, contactImportResolve.js,
  // contactImportSalesMap.js) are UNCHANGED — same exports, same tests, same behavior. What
  // changes is entirely in THIS file's own orchestration:
  //
  // `contactImportResolve.js` still requires a `decisions.unusableSales[shopKey]` answer
  // (`{action:"provide", name}` or `{action:"skip"}`) before a shop with an unreadable Sales
  // cell can reach `plan` at all — that contract is untouched. This file now supplies that
  // answer AUTOMATICALLY, always `{action:"provide", name: <a synthetic, per-row-unique name>}`
  // (a NUL-prefixed string no real typed Sales name could ever collide with), so the shop
  // reaches `plan` without the user ever being asked for a NAME at that layer. The real decision
  // — which PERSON — is made in step 5 against that synthetic name's own key.
  //
  // The one genuinely new piece of logic is "choosing directly from people: the ones mapped or
  // created in this step, plus the app's existing persons" — and it deliberately does NOT let a
  // direct-person-pick row reference a person transiently created by ANOTHER decision in the
  // same preview pass. `generateId()` (`utils/idGenerators.js`) is `Date.now()+Math.random()` —
  // not deterministic — so a person "created" inside a `useMemo` gets a DIFFERENT id every time
  // that memo recomputes (e.g. when an unrelated decision changes). Storing such a transient id
  // into persistent decision state would silently go stale the next time anything recomputes,
  // failing only at the real commit. Two SAFE mechanisms instead:
  //   - "Pilih Orang" -> an EXISTING app person (`data.persons`, a real, stable id) -> the row's
  //     OWN key resolves via a normal `{action:"map", personId}`.
  //   - "Sama seperti sales lain di langkah ini" -> reuses that OTHER Sales name's own RAW TEXT
  //     as THIS shop's `salesNames` entry (never the synthetic one) — `collectSalesNamesNeeded`
  //     then merges them into the exact same name-entry, resolved by whatever that other name's
  //     own decision already is (map OR create), entirely within `resolveSalesPersonMapping`'s
  //     own single, already-safe accumulating pass. No id is ever stored by this file at all.
  //   - "Buat Orang Baru" -> its own independent `{action:"create", name, code}`, safe for the
  //     same reason any other create is: resolved once, atomically, inside that same single pass.
  const unusableSalesRows = useMemo(() => {
    const entries = [];
    for (const p of (parsed ? parsed.problems : [])) {
      if (p.type !== "unusable-sales") continue;
      const decisionKey = shopKey(p.sheet, p.row);
      const syntheticName = ` UNUSABLE ${decisionKey}`;
      entries.push({
        sheet: p.sheet, row: p.row, shopName: p.shopName, decisionKey,
        syntheticName, nameKey: salesNameKey(syntheticName),
      });
    }
    return {
      entries,
      nameKeySet: new Set(entries.map((e) => e.nameKey)),
    };
  }, [parsed]);

  const resolvedUnusable = useMemo(() => {
    const auto3a = {};
    const salesMapPatch = {};
    for (const e of unusableSalesRows.entries) {
      const choice = unusablePersonChoices[e.decisionKey];
      if (choice && choice.mode === "same" && choice.nameKey && regularNameTextByKey.has(choice.nameKey)) {
        auto3a[e.decisionKey] = { action: "provide", name: regularNameTextByKey.get(choice.nameKey) };
        continue; // this shop's own salesNames entry IS the regular name's text now — no synthetic key needed
      }
      auto3a[e.decisionKey] = { action: "provide", name: e.syntheticName };
      if (choice) {
        if (choice.mode === "existing" && choice.personId) {
          salesMapPatch[e.nameKey] = { action: "map", personId: choice.personId };
        } else if (choice.mode === "create") {
          salesMapPatch[e.nameKey] = { action: "create", name: choice.name, code: choice.code };
        } else if (choice.mode === "skip") {
          salesMapPatch[e.nameKey] = { action: "skip" };
        }
      }
    }
    return { auto3a, salesMapPatch };
  }, [unusableSalesRows, unusablePersonChoices, regularNameTextByKey]);

  const decisions3a = useMemo(
    () => ({
      duplicateNames: duplicateDecisions,
      extraAddresses: addressDecisions,
      unusableSales: resolvedUnusable.auto3a,
      missingAddress: missingAddressDecisions,
      city: cityDecisions,
      secondSales: secondSalesDecisions,
    }),
    [duplicateDecisions, addressDecisions, resolvedUnusable, missingAddressDecisions, cityDecisions, secondSalesDecisions]
  );

  // The real gate for every step's own "Lanjut" — reuses resolveContactImport's own
  // determination of what's still open, never a second, hand-rolled "is this answered" check.
  const resolved = useMemo(() => (parsed ? resolveContactImport(parsed, decisions3a) : null), [parsed, decisions3a]);
  const unresolvedByType = useMemo(() => {
    const by = {};
    for (const u of (resolved && resolved.unresolved) || []) {
      (by[u.type] = by[u.type] || []).push(u);
    }
    return by;
  }, [resolved]);
  const blockedCount = (type) => (unresolvedByType[type] || []).length;

  // ── Step 6 (batch 5b): salesperson mapping ──────────────────────────────────────────────────

  // Every distinct Sales name still needing a decision — one entry per NAME, cross-sheet,
  // case/whitespace-merged (`salesNameKey`), sourced from `resolved.plan` (batch 3a's own fully-
  // resolved rows, each carrying `salesNames`) via `collectSalesNamesNeeded` — never re-derived
  // here. Split into REGULAR names and the still-synthetic unusable-Sales ones (a shop resolved
  // via "same as" never appears in the synthetic half at all, since its own `salesNames` entry
  // IS the regular name's text by that point).
  const salesNamesNeeded = useMemo(
    () => collectSalesNamesNeeded(resolved ? resolved.plan : []),
    [resolved]
  );
  const regularSalesNames = useMemo(
    () => salesNamesNeeded.filter((e) => !unusableSalesRows.nameKeySet.has(e.key)),
    [salesNamesNeeded, unusableSalesRows]
  );

  // The real gate for step 5's own "Lanjut" — reuses resolveSalesPersonMapping's own
  // determination directly, same discipline as `resolved` above. Pure: this never actually
  // writes anywhere, including on every keystroke while editing a decision — only the FINAL
  // "Impor Sekarang" click (`runCommit`, below) ever reaches `onCommit`. ONE combined call —
  // regular decisions and the unusable-Sales rows' own `salesMapPatch` are just more entries in
  // the SAME flat decisions map, resolved together in the SAME safe, single accumulating pass.
  const salesMapResult = useMemo(
    () => resolveSalesPersonMapping(data, resolved ? resolved.plan : [], { ...salesMapDecisions, ...resolvedUnusable.salesMapPatch }),
    [data, resolved, salesMapDecisions, resolvedUnusable]
  );

  // ── Step 7 (batch 5c): firm grouping — the LAST review step, deliberately, because it needs
  // the FINAL list of shops (`salesMapResult.plan`) — any earlier step (duplicates, addresses,
  // missing values, Sales mapping) can change which shops even exist or what they're named, and
  // this step's whole job is proposing which of THOSE shops are the same real firm.
  //
  // INVALIDATION: exactly the same principle every earlier step's derived value already follows
  // (`resolved`/`salesMapResult`/`commitPreview` are all `useMemo`s that recompute the instant
  // an upstream decision changes) — applied here to a value too expensive to be a live `useMemo`.
  // Comparing `groupingResult.forPlan` against the CURRENT `salesMapResult.plan` by reference
  // catches ANY upstream change automatically (a new plan array is a new reference, always, since
  // every function in the chain is pure) — no separate "did something change" tracking needed.
  // Unlike correction 1's step-2-kind-change gate (which asks first, because it would silently
  // discard a human's own TYPED answer at another step), invalidating a stale grouping proposal
  // needs no confirm: it is a COMPUTED PROPOSAL over the shop list, not a fact about one specific
  // shop, so once the shop list itself has changed, the old proposal (and whatever confirm/
  // reject/name a human gave it) genuinely no longer describes anything real — keeping it would
  // be the wrong default, not a considerate one. Re-entering this step after an invalidation
  // simply shows the same "not computed yet" state as a first visit, and pays the ~5s cost again.
  useEffect(() => {
    if (step !== 7 || !parsed || groupComputing) return;
    const plan = salesMapResult.plan;
    if (groupingResult && groupingResult.forPlan === plan) return; // already fresh for this exact plan
    setGroupComputing(true);
    setTimeout(() => {
      const result = groupProposedFirms(plan);
      setGroupingResult({ groups: result.groups, unmatched: result.unmatched, forPlan: plan });
      setGroupDecisions({});
      setGroupComputing(false);
    }, 0);
  }, [step, parsed, salesMapResult.plan, groupingResult, groupComputing]);

  // ── Step 8: summary + commit ─────────────────────────────────────────────────────────────────

  // The exact same pure `commitContactImport` the real commit uses, run here purely for PREVIEW
  // — "a preview, not the result" (the task's own framing): since every step of this chain is
  // pure and side-effect-free, running it now costs nothing and guarantees the preview can never
  // structurally disagree with what actually commits a moment later off the SAME inputs.
  const commitPreview = useMemo(
    () => commitContactImport(salesMapResult.data, salesMapResult.plan),
    [salesMapResult]
  );
  const summary = useMemo(
    () => buildImportSummary(resolved || { excluded: [] }, salesMapResult, commitPreview, data.persons),
    [resolved, salesMapResult, commitPreview, data]
  );
  // Same "preview, not the result" reasoning as `commitPreview` above — `applyConfirmedGroups`
  // is cheap (it only ever iterates the ALREADY-computed `groupingResult.groups`, never re-scores
  // anything), so unlike `groupProposedFirms` itself it stays a live, always-fresh `useMemo`.
  const groupApplyPreview = useMemo(
    () => applyConfirmedGroups(commitPreview.data, groupingResult ? groupingResult.groups : [], groupDecisions, commitPreview.report),
    [commitPreview, groupingResult, groupDecisions]
  );

  // Step 2's own master group list — every duplicate-name group in the file, independent of
  // whatever's been decided so far (batch 5a addition: collectDecisionsNeeded's second,
  // optional `decisions` argument defaults to `{}`, which is exactly what's wanted here — the
  // full, stable list, never narrowed by an already-made answer).
  const duplicateGroupDefs = useMemo(() => (parsed ? collectDecisionsNeeded(parsed).duplicateNames : []), [parsed]);

  // Steps 3/4's own enumeration — "what rows have this ambiguity AT ALL", given step 2's
  // decisions applied (merging changes which rows even have >1 address / 0 addresses) but
  // steps 3/4's own categories deliberately left unanswered in THIS call, so an already-decided
  // row still appears here (to review/edit), distinct from unresolvedByType's "still blocking"
  // count above.
  const partialAfterDup = useMemo(
    () => (parsed ? collectDecisionsNeeded(parsed, { duplicateNames: duplicateDecisions }) : null),
    [parsed, duplicateDecisions]
  );

  // Step 5's own unusable-Sales section shows the PARSER's problem message
  // (contactImportParser.js), which carries the real diagnostic (`reason`/`found`, e.g. "selnya
  // berisi galat Excel (#N/A)") — never resolveSalesPersonMapping's own generic message for the
  // row's synthetic name, which would be unreadable (it's a NUL-prefixed internal token, never
  // meant to be displayed).
  const salesProblemByKey = useMemo(() => {
    const m = new Map();
    for (const p of (parsed ? parsed.problems : [])) {
      if (p.type === "unusable-sales") m.set(shopKey(p.sheet, p.row), p);
    }
    return m;
  }, [parsed]);

  // Same reasoning, for step 4's own underived-city rows — the parser's own `city-not-derived`
  // problem carries the shop's raw `address`, so a human filling in a real city can actually read
  // the postcode/province/district text that failed to derive, instead of guessing blind.
  const cityProblemByKey = useMemo(() => {
    const m = new Map();
    for (const p of (parsed ? parsed.problems : [])) {
      if (p.type === "city-not-derived") m.set(shopKey(p.sheet, p.row), p);
    }
    return m;
  }, [parsed]);

  // Batch 3a's own spec for an already-derived city: "confirm, correct, or leave blank" — never
  // just shown read-only. Built via the shared `buildEffectiveShops` export (never re-derived
  // here) so this list's row identity (sheet+anchorRow -> shopKey) is guaranteed to agree with
  // resolveContactImport's own city-decision key — the same effective shop, not a second guess
  // at what "the shop" means once a duplicate-name group has been merged/separated.
  const effectiveShops = useMemo(
    () => (parsed ? buildEffectiveShops(parsed, duplicateDecisions).effectiveShops : []),
    [parsed, duplicateDecisions]
  );
  const derivedCityRows = useMemo(
    () => effectiveShops.filter((eff) => eff.city.derived),
    [effectiveShops]
  );

  const remainingCityCount = useMemo(() => {
    const rows = partialAfterDup ? partialAfterDup.cityNotDerived || [] : [];
    return rows.filter((e) => !Object.prototype.hasOwnProperty.call(cityDecisions, e.decisionKey)).length;
  }, [partialAfterDup, cityDecisions]);

  // Manual-testing fix — same "leave it empty" affordance city already had, now for missing
  // address too: an untouched field (never typed in, no key in the map at all) and a
  // deliberately-empty one ({text:""}, a key IS present) must stay distinct at the data level —
  // this only counts rows genuinely still untouched.
  const remainingAddressCount = useMemo(() => {
    const rows = partialAfterDup ? partialAfterDup.missingAddress || [] : [];
    return rows.filter((e) => !Object.prototype.hasOwnProperty.call(missingAddressDecisions, e.decisionKey)).length;
  }, [partialAfterDup, missingAddressDecisions]);

  const totalDecisionsCount =
    Object.keys(duplicateDecisions).length +
    Object.keys(addressDecisions).length +
    Object.keys(missingAddressDecisions).length +
    Object.keys(cityDecisions).length +
    Object.keys(secondSalesDecisions).length +
    Object.keys(salesMapDecisions).length +
    Object.keys(unusablePersonChoices).length +
    Object.keys(groupDecisions).length;

  // ── Manual-testing fix — the pinned footer's own "what's unanswered" list, per step ─────────
  // One line per distinct ambiguity TYPE still open on the CURRENT step, each naming a real count
  // ("4 alamat kosong belum dijawab") and jumping to the first still-unanswered row of that type
  // — this is what actually answers "the page said only 'Selesaikan semua...' across 600+ rows,"
  // the real block a manual-testing pass hit (four missing addresses, no way to tell which).
  const step2Items = useMemo(() => {
    const all = [...(unresolvedByType["duplicate-name"] || []), ...(unresolvedByType["duplicate-name-conflict"] || [])];
    if (all.length === 0) return [];
    return [{ key: "dup", label: `${all.length} nama toko ganda belum diputuskan`, targetId: `group-${all[0].decisionKey}` }];
  }, [unresolvedByType]);

  const step3Items = useMemo(() => {
    const list = unresolvedByType["extra-address"] || [];
    if (list.length === 0) return [];
    return [{ key: "addr", label: `${list.length} alamat tambahan belum diputuskan`, targetId: `extra-${list[0].decisionKey}` }];
  }, [unresolvedByType]);

  const step4Items = useMemo(() => {
    const items = [];
    const missing = unresolvedByType["missing-address"] || [];
    if (missing.length > 0) {
      items.push({ key: "missing-addr", label: `${missing.length} alamat kosong belum dijawab`, targetId: `missing-addr-${missing[0].decisionKey}` });
    }
    const city = unresolvedByType["city-not-derived"] || [];
    if (city.length > 0) {
      items.push({ key: "city", label: `${city.length} kota belum diketahui belum dijawab`, targetId: `city-${city[0].decisionKey}` });
    }
    return items;
  }, [unresolvedByType]);

  // Step 5 (the new "Sales Tambahan" step) — every still-unanswered `possible-second-sales`
  // question. Reuses `unresolvedByType` directly, same as every other step's own footer items.
  const step5Items = useMemo(() => {
    const list = unresolvedByType["possible-second-sales"] || [];
    if (list.length === 0) return [];
    return [{
      key: "second-sales",
      label: `${list.length} kemungkinan sales kedua belum diputuskan`,
      targetId: `second-sales-${list[0].decisionKey}`,
    }];
  }, [unresolvedByType]);

  const step6Items = useMemo(() => {
    const items = [];
    const unresolvedKeys = new Set((salesMapResult.unresolved || []).map((u) => u.key));
    const regularLeft = regularSalesNames.filter((e) => unresolvedKeys.has(e.key));
    if (regularLeft.length > 0) {
      items.push({ key: "sales-regular", label: `${regularLeft.length} nama sales belum dipetakan`, targetId: `sales-${regularLeft[0].key}` });
    }
    const unusableLeft = unusableSalesRows.entries.filter((e) => {
      const choice = unusablePersonChoices[e.decisionKey];
      if (choice && choice.mode === "same") return false; // resolved via the regular name it now shares
      return unresolvedKeys.has(e.nameKey);
    });
    if (unusableLeft.length > 0) {
      items.push({
        key: "sales-unusable",
        label: `${unusableLeft.length} toko dengan sales tidak terbaca belum dijawab`,
        targetId: `unusable-${unusableLeft[0].decisionKey}`,
      });
    }
    return items;
  }, [salesMapResult, regularSalesNames, unusableSalesRows, unusablePersonChoices]);

  const footerItems = step === 2 ? step2Items : step === 3 ? step3Items : step === 4 ? step4Items
    : step === 5 ? step5Items : step === 6 ? step6Items : [];
  const footerCanAdvance = (() => {
    switch (step) {
      case 1: return !!parsed && parsed.problems.filter((p) => p.severity === "error").length === 0;
      case 2: return blockedCount("duplicate-name") + blockedCount("duplicate-name-conflict") === 0;
      case 3: return blockedCount("extra-address") === 0;
      // Scoped to exactly step 4's own two questions (matches step4Items above) — NOT
      // `resolved.readyToCommit`, which also counts `possible-second-sales` (step 5's own
      // question, answered later) among its unresolved items. unusable-sales is excluded too,
      // but needs no explicit filter here: it's always pre-answered via `resolvedUnusable.auto3a`
      // before it ever reaches `resolved.unresolved` (see that memo's own comment) — step 5
      // (possible-second-sales) has no such auto-answer, so it must be filtered explicitly.
      case 4: return blockedCount("missing-address") + blockedCount("city-not-derived") === 0;
      case 5: return blockedCount("possible-second-sales") === 0;
      case 6: return salesMapResult.readyToCommit;
      case 7: return true; // firm grouping never blocks — leaving a group undecided is legal
      default: return true;
    }
  })();

  // ── Step 1: file read, with the raw-error-cell substitution ────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setParseError("");
    setParsed(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = read(ev.target.result, { cellDates: true });
        const sheets = wb.SheetNames.map((name) => {
          const ws = wb.Sheets[name];
          const rawRows = xlsxUtils.sheet_to_json(ws, { header: 1, raw: true, defval: null });
          // The one substitution contactImportParser.js's own header says a reading layer must
          // do: sheet_to_json collapses a real Excel error cell (a broken "#N/A" formula) to a
          // bare null in the Sales column before this parser ever sees it — patch it back from
          // the raw worksheet, so the two #N/A shops in the real file keep their diagnostic text.
          return { name, rows: patchSalesErrorCells(ws, rawRows) };
        });
        setParsed(parseContactWorkbook(sheets));
      } catch {
        setParseError("Gagal membaca file. Pastikan file adalah .xlsx yang valid.");
      }
    };
    reader.onerror = () => setParseError("Gagal membaca file. Pastikan file adalah .xlsx yang valid.");
    reader.readAsArrayBuffer(file);
  };

  const structuralErrors = parsed ? parsed.problems.filter((p) => p.severity === "error") : [];
  const cabangShops = parsed && parsed.sheets.CABANG ? parsed.sheets.CABANG.shops : null;
  const pusatShops = parsed && parsed.sheets.PUSAT ? parsed.sheets.PUSAT.shops : null;
  const warningCount = parsed ? parsed.problems.filter((p) => p.severity === "warning").length : 0;

  // ── Step 2: duplicate names ─────────────────────────────────────────────────────────────────

  function memberShops(entry) {
    const shops = (parsed.sheets[entry.sheet] && parsed.sheets[entry.sheet].shops) || [];
    return shops.filter((s) => entry.rows.includes(s.row));
  }

  /** What name a sheet's OTHER shops currently, effectively, carry — given step 2's decisions
   *  so far. A row not in any duplicate group always carries its own original name; a row in a
   *  "separate"-decided group carries its own typed rename (or its original name, until
   *  renamed); a row in a "merge"-decided (or still-undecided) group carries the group's shared
   *  original name — merging never renames anything, so this is safe either way. */
  function currentEffectiveName(shop, sheet) {
    const group = duplicateGroupDefs.find((g) => g.sheet === sheet && g.rows.includes(shop.row));
    if (!group) return shop.name;
    const decision = normalizeDuplicateDecision(duplicateDecisions[group.decisionKey]);
    if (decision.kind === "separate") {
      const renamed = decision.names && decision.names[shop.row];
      return (renamed && String(renamed).trim()) || shop.name;
    }
    return shop.name;
  }

  /** Live preview only — the real gate stays resolveContactImport's own duplicate-name-conflict
   *  check (unresolvedByType) plus commitContactImport's own nameCollides check at actual
   *  commit time (a later batch); this exists so a colliding rename is flagged the instant it's
   *  typed, not only once the resolver runs on blur/Lanjut. */
  function renameCollision(sheet, row, candidateName) {
    const norm = normName(candidateName);
    if (!norm) return null;
    const shops = (parsed.sheets[sheet] && parsed.sheets[sheet].shops) || [];
    for (const s of shops) {
      if (s.row === row) continue;
      if (normName(currentEffectiveName(s, sheet)) === norm) return { withRow: s.row };
    }
    if (nameCollides(data.contacts || [], sheet, candidateName, null)) return { existing: true };
    return null;
  }

  function setDuplicateRename(entry, row, value) {
    setDuplicateDecisions((prev) => {
      const cur = normalizeDuplicateDecision(prev[entry.decisionKey]);
      return { ...prev, [entry.decisionKey]: { kind: "separate", names: { ...cur.names, [row]: value } } };
    });
  }

  function shopKeysForGroup(entry) {
    return entry.rows.map((r) => shopKey(entry.sheet, r));
  }

  function countDownstreamForKeys(keys) {
    const keySet = new Set(keys);
    let n = 0;
    for (const map of [addressDecisions, missingAddressDecisions, cityDecisions]) {
      for (const k of Object.keys(map)) if (keySet.has(k)) n += 1;
    }
    return n;
  }

  function clearDownstreamForKeys(keys) {
    const keySet = new Set(keys);
    const strip = (obj) => {
      const next = {};
      for (const k of Object.keys(obj)) if (!keySet.has(k)) next[k] = obj[k];
      return next;
    };
    setAddressDecisions(strip);
    setMissingAddressDecisions(strip);
    setCityDecisions(strip);
  }

  /** Correction 1: changing an already-decided group's kind (merge<->separate, or a first
   *  decision) is applied immediately ONLY when it costs nothing (no downstream answer exists
   *  yet for any of this group's rows). The instant it WOULD orphan a real, typed answer in
   *  step 3 or 4, this asks first, names the exact count, and applies BOTH the kind change and
   *  the clearing together on confirm — or neither at all on decline. A plain rename (the kind
   *  staying "separate") never reaches this path: a row's own shopKey is `sheet:row`, unaffected
   *  by what it's named, so renaming alone can never orphan a step-3/4 answer. Unusable-Sales
   *  (now step 5) is NOT part of this "downstream" set — a row's own Sales value/decision is
   *  intrinsic to that row and unaffected by whether its group is merged or kept separate. */
  function requestDuplicateKind(entry, newKind) {
    const current = normalizeDuplicateDecision(duplicateDecisions[entry.decisionKey]);
    if (current.kind === newKind) return;
    const keys = shopKeysForGroup(entry);
    const n = countDownstreamForKeys(keys);
    const applyKind = () => {
      setDuplicateDecisions((prev) => ({
        ...prev,
        [entry.decisionKey]: newKind === "separate" ? { kind: "separate", names: {} } : { kind: "merge" },
      }));
      if (n > 0) clearDownstreamForKeys(keys);
    };
    if (n > 0) {
      setPendingDuplicateChange({
        message: `Mengubah ini akan menghapus ${n} keputusan di langkah 3–4. Lanjutkan?`,
        onConfirm: () => {
          applyKind();
          setPendingDuplicateChange(null);
        },
      });
    } else {
      applyKind();
    }
  }

  // ── Step 3: extra addresses ─────────────────────────────────────────────────────────────────

  function setAddressAction(key, action, dropIndices) {
    setAddressDecisions((prev) => ({ ...prev, [key]: { action, dropIndices: dropIndices || [] } }));
  }
  function toggleDropIndex(entry, idx, checked) {
    const cur = normalizeAddressDecision(addressDecisions[entry.decisionKey]);
    const nextDrop = checked
      ? [...new Set([...cur.dropIndices, idx])]
      : cur.dropIndices.filter((i) => i !== idx);
    setAddressAction(entry.decisionKey, "dedupe", nextDrop);
  }

  // ── Step 4: missing address / underived city ────────────────────────────────────────────────

  /** Manual-testing fix (round 4 — bug, not an acceptable inference): typing real text then
   *  backspacing it all the way to "" must NOT produce {text:""} — that's the deliberate-blank
   *  decision, and reaching it by clearing a typo would (a) silently record "deliberately empty"
   *  for a row the user never actually chose to leave blank, and (b) disable this very input
   *  mid-edit the instant the last character is deleted, locking the user out of the field they
   *  were typing in. Only the "Biarkan kosong" tickbox (`setMissingAddressEmpty` below) may ever
   *  write {text:""}. Backspacing to empty instead calls `clearMissingAddressDecision` — the
   *  SAME deletion unticking already does — so the row genuinely returns to UNANSWERED and the
   *  input stays enabled throughout. The tickbox's own `checked` stays a derived read
   *  (`answered && text===""`) with no second source of truth: since typing can never itself
   *  produce that state, deriving from it remains correct. */
  function setMissingAddressText(entry, text) {
    if (text === "") { clearMissingAddressDecision(entry); return; }
    setMissingAddressDecisions((prev) => ({ ...prev, [entry.decisionKey]: { text } }));
  }
  /** Manual-testing fix: an explicit "biarkan kosong" reaches the same {text:""} decision typing
   *  nothing and never blurring never could — see this function's own city-side twin below. */
  function setMissingAddressEmpty(entry) {
    setMissingAddressDecisions((prev) => ({ ...prev, [entry.decisionKey]: { text: "" } }));
  }
  /** Manual-testing fix (round 3) — the "Biarkan kosong" tickbox's own OFF transition: a one-way
   *  button could reach {text:""} but never go back. Unticking must return the row to genuinely
   *  UNANSWERED (the key absent from the map), never just some other value at the key — this is
   *  the one place presence itself is removed, not merely overwritten, mirroring every other
   *  "presence, not truthiness" read of these decision maps elsewhere in this file. Also called
   *  directly by `setMissingAddressText` above (round 4) when typed text reaches "". */
  function clearMissingAddressDecision(entry) {
    setMissingAddressDecisions((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, entry.decisionKey)) return prev;
      const next = { ...prev };
      delete next[entry.decisionKey];
      return next;
    });
  }
  function leaveRemainingAddressesEmpty() {
    const rows = partialAfterDup ? partialAfterDup.missingAddress || [] : [];
    setMissingAddressDecisions((prev) => {
      const next = { ...prev };
      for (const entry of rows) {
        if (!Object.prototype.hasOwnProperty.call(next, entry.decisionKey)) next[entry.decisionKey] = { text: "" };
      }
      return next;
    });
  }
  // Used ONLY by the "Kota dari Alamat" derived-city override section, below — a derived row is
  // never "unanswered" (it always has at least the auto-derived value), so typing it down to ""
  // is a deliberate, literal override to blank (batch 3a's own "confirm, correct, or leave
  // blank" spec, unchanged) — never a delete-the-key/revert-to-derived action. Do NOT reuse this
  // for the "Kota Belum Diketahui" tickbox rows — see `setUnderivedCityText` just below for why
  // those need the opposite behavior on an empty string.
  function setCityText(key, city) {
    setCityDecisions((prev) => ({ ...prev, [key]: { city } }));
  }
  /** City's own twin of `setMissingAddressText` (round 4) — for the "Kota Belum Diketahui"
   *  tickbox rows specifically. Typing real text then backspacing to "" must return the row to
   *  UNANSWERED (delete the key), never silently record a deliberate blank the user didn't
   *  choose via the tickbox — same bug/fix reasoning as that function's own comment. Deliberately
   *  a SEPARATE function from `setCityText` above: that one backs the DERIVED-city override
   *  section, where typing to "" is itself the deliberate override, not an "unanswered" concept
   *  that doesn't exist there. */
  function setUnderivedCityText(entry, city) {
    if (city === "") { clearCityDecision(entry); return; }
    setCityDecisions((prev) => ({ ...prev, [entry.decisionKey]: { city } }));
  }
  /** City's own twin of `setMissingAddressEmpty` — the "Biarkan kosong" tickbox's ON transition. */
  function setCityEmpty(entry) {
    setCityDecisions((prev) => ({ ...prev, [entry.decisionKey]: { city: "" } }));
  }
  /** City's own twin of `clearMissingAddressDecision` — see that function's own comment. Also
   *  called directly by `setUnderivedCityText` above (round 4) when typed text reaches "". */
  function clearCityDecision(entry) {
    setCityDecisions((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, entry.decisionKey)) return prev;
      const next = { ...prev };
      delete next[entry.decisionKey];
      return next;
    });
  }

  /** Bulk action for step 4's own "Kota Belum Diketahui" list — fills every question NOT yet
   *  answered with a real, deliberate blank ({city: ""}), same shape as a human typing nothing
   *  and moving on — which, per-row, is exactly what ticking that row's own "Biarkan kosong" box
   *  does, so a row this bulk action just blanked shows that same box already ticked and stays
   *  individually untickable afterward (manual-testing fix, round 3: the box being a REAL,
   *  two-way reflection of the decision map, never separate UI-only state, is what makes this
   *  true for free). Never touches a row that already has a real answer — this only ever fills
   *  gaps, it can't undo a typed value. Blank is a valid answer (`contactImportResolve.js`'s own
   *  contract); this button exists because moving ~130 real PUSAT rows into this list (once
   *  digit-tail addresses stopped being treated as cities) made answering each one by hand
   *  impractical for a one-time import. Typing into a row it just blanked works exactly like
   *  typing into any other row — `cityDecisions[key]` is a plain, later write, never re-blanked
   *  by anything else — and that value is what reaches the eventual plan (see
   *  `ImportKontakWizard.test.js`'s own explicit regression test for this). */
  function leaveRemainingCitiesEmpty() {
    const rows = partialAfterDup ? partialAfterDup.cityNotDerived || [] : [];
    setCityDecisions((prev) => {
      const next = { ...prev };
      for (const entry of rows) {
        if (!Object.prototype.hasOwnProperty.call(next, entry.decisionKey)) next[entry.decisionKey] = { city: "" };
      }
      return next;
    });
  }

  // ── Step 5: possible second sales (gap fix — a continuation row's own Sales, previously
  // detected by contactImportParser.js and read by nothing, is now a real decision) ────────────

  /** ADD folds the continuation row's own name into `salesNames` alongside the parent's; KEEP
   *  leaves the shop with only the parent's own name — today's pre-existing silent behavior, now
   *  an explicit, reversible choice instead. Default nothing: no button starts active. */
  function setSecondSalesAction(key, action) {
    setSecondSalesDecisions((prev) => ({ ...prev, [key]: { action } }));
  }

  // ── Step 6: regular salesperson mapping ─────────────────────────────────────────────────────

  function setSalesMapAction(key, action, fields) {
    setSalesMapDecisions((prev) => ({ ...prev, [key]: { action, ...fields } }));
  }

  function codeFormatValid(code) {
    return CODE_FORMAT.test(code || "");
  }

  /** Live, specific feedback beyond the resolver's own single generic message — checks the SAME
   *  things `resolveSalesPersonMapping`'s own `addPerson` call would refuse on (an existing
   *  person's code, or a code collision with ANOTHER "create" decision in progress — a regular
   *  name's own, or an unusable-Sales row's own), but names which one, since "kode tidak valid/
   *  sudah dipakai" alone doesn't say where the collision actually is. The real gate stays
   *  `salesMapResult.readyToCommit`. */
  function codeCollision(entryKey, code) {
    if (!code) return null;
    const upper = code.toUpperCase();
    if ((data.persons || []).some((p) => p.code === upper)) return { type: "existing" };
    for (const other of regularSalesNames) {
      if (other.key === entryKey) continue;
      const d = normalizeSalesMapDecision(salesMapDecisions[other.key]);
      if (d.action === "create" && (d.code || "").toUpperCase() === upper) {
        return { type: "duplicate", withName: other.name };
      }
    }
    for (const e of unusableSalesRows.entries) {
      const choice = unusablePersonChoices[e.decisionKey];
      if (choice && choice.mode === "create" && (choice.code || "").toUpperCase() === upper) {
        return { type: "duplicate", withName: e.shopName };
      }
    }
    return null;
  }

  /** SKIP must say what it costs BEFORE the user commits to it — a zero-cost skip (every shop
   *  using this name has some other salesperson too) applies immediately, same "don't warn when
   *  there's nothing to lose" discipline `requestDuplicateKind` already uses; a real cost shows a
   *  `ConfirmModal` naming the exact shop count, and does nothing at all until confirmed. */
  function requestSkipSalesName(entry) {
    const cost = skipCost(entry, resolved ? resolved.plan : []);
    if (cost > 0) {
      setPendingSkipSales({
        message: `${cost} toko tidak akan dibuat karena "${entry.name}" adalah satu-satunya sales mereka. Lewati "${entry.name}"?`,
        onConfirm: () => {
          setSalesMapAction(entry.key, "skip", {});
          setPendingSkipSales(null);
        },
      });
    } else {
      setSalesMapAction(entry.key, "skip", {});
    }
  }

  // ── Step 6: unusable Sales, resolved directly against people (manual-testing fix) ──────────

  function setUnusableChoice(rowKey, value) {
    setUnusablePersonChoices((prev) => ({ ...prev, [rowKey]: value }));
  }
  /** The combined "Pilih Orang" select's own onChange — decodes which of the two safe option
   *  groups was picked (see this file's own header comment for why there are exactly two). */
  function selectUnusablePerson(rowKey, raw) {
    if (raw.startsWith("person:")) {
      setUnusableChoice(rowKey, { mode: "existing", personId: raw.slice("person:".length) });
    } else if (raw.startsWith("name:")) {
      setUnusableChoice(rowKey, { mode: "same", nameKey: raw.slice("name:".length) });
    } else {
      setUnusableChoice(rowKey, { mode: "existing", personId: "" });
    }
  }
  function unusableCodeCollision(rowKey, code) {
    if (!code) return null;
    const upper = code.toUpperCase();
    if ((data.persons || []).some((p) => p.code === upper)) return { type: "existing" };
    for (const entry of regularSalesNames) {
      const d = normalizeSalesMapDecision(salesMapDecisions[entry.key]);
      if (d.action === "create" && (d.code || "").toUpperCase() === upper) return { type: "duplicate", withName: entry.name };
    }
    for (const e of unusableSalesRows.entries) {
      if (e.decisionKey === rowKey) continue;
      const choice = unusablePersonChoices[e.decisionKey];
      if (choice && choice.mode === "create" && (choice.code || "").toUpperCase() === upper) {
        return { type: "duplicate", withName: e.shopName };
      }
    }
    return null;
  }

  /** Manual-testing fix — the "Sama seperti sales lain di langkah ini" list used to show the raw
   *  FILE name, which can differ from what that name actually resolves to (mapped to a different-
   *  named existing person, or created under an edited/renamed name) — the screen showed one name
   *  and stored another. This shows the real OUTCOME instead: "{fileName} -> {realName} (CODE)"
   *  for a resolved map/create, ", orang yang sudah ada" appended only for "map" (an EXISTING
   *  person) since "create" is already self-evidently a new one. A `skip` decision is reported
   *  "dilewati" and marked unpickable — picking it would silently exclude this shop the same way
   *  it excludes every other shop depending on that name, with no visible sign why. Anything else
   *  (no decision yet, a "map" with no person chosen yet, a "create" with an incomplete name/code)
   *  says "belum diputuskan" but stays pickable — unlike skip, this isn't a silent bad outcome:
   *  `collectSalesNamesNeeded` just merges this shop under that name's key, and whatever the name
   *  eventually resolves to (or the fact it's still unresolved) applies to both, visibly, the same
   *  way the existing row-225-class shared-name case already works. */
  function regularNameOutcome(entry) {
    const decision = normalizeSalesMapDecision(salesMapDecisions[entry.key]);
    if (decision.action === "skip") {
      return { label: `${entry.name} — dilewati`, pickable: false };
    }
    if (decision.action === "map" && decision.personId) {
      const person = (data.persons || []).find((p) => p.id === decision.personId);
      if (person) {
        return { label: `${entry.name} -> ${person.name} (${person.code}), orang yang sudah ada`, pickable: true };
      }
    }
    if (decision.action === "create" && (decision.name || "").trim() && (decision.code || "").trim()) {
      return { label: `${entry.name} -> ${decision.name.trim()} (${decision.code.trim()})`, pickable: true };
    }
    return { label: `${entry.name} — belum diputuskan`, pickable: true };
  }

  // ── Step 7: firm grouping (batch 5c) ────────────────────────────────────────────────────────

  function groupMemberKey(shop) {
    return shopKey(shop.list, shop.row);
  }

  /** A group's own members, minus whatever this human has removed — the SAME filter
   *  `contactImportApplyGroups.js`'s own `applyConfirmedGroups` applies at commit time, kept in
   *  sync by construction (both read `groupMemberKey`/`shopKey`, never a re-derived comparison),
   *  so what's shown here as "2 anggota tersisa, cukup untuk dikonfirmasi" can never disagree
   *  with what actually gets confirmed a moment later. */
  function effectiveGroupMembers(group, groupIndex) {
    const removed = new Set((groupDecisions[groupIndex] && groupDecisions[groupIndex].removedKeys) || []);
    return group.members.filter((m) => !removed.has(groupMemberKey(m.shop)));
  }

  function setGroupName(groupIndex, name) {
    setGroupDecisions((prev) => ({ ...prev, [groupIndex]: { ...(prev[groupIndex] || {}), name } }));
  }

  /** CONFIRM / REJECT — never pre-fills or suggests a name (the task's own explicit rule); the
   *  Konfirmasi control itself is disabled in the JSX below until a real name is typed AND at
   *  least two effective members remain, but `applyConfirmedGroups` re-checks both independently
   *  at commit time regardless (defense in depth, this file's own established discipline). */
  function setGroupStatus(groupIndex, status) {
    setGroupDecisions((prev) => ({ ...prev, [groupIndex]: { ...(prev[groupIndex] || {}), status } }));
  }

  /** REMOVE a member, one click, no confirm — reversible only by leaving and re-entering this
   *  step (which recomputes the whole proposal from scratch); see the task's own framing: this is
   *  a lightweight edit to a computed suggestion, not a destructive action on real data (nothing
   *  is written anywhere until "Impor Sekarang"). */
  function removeGroupMember(groupIndex, shop) {
    setGroupDecisions((prev) => {
      const cur = prev[groupIndex] || {};
      const removed = new Set(cur.removedKeys || []);
      removed.add(groupMemberKey(shop));
      return { ...prev, [groupIndex]: { ...cur, removedKeys: [...removed] } };
    });
  }

  // ── Step 8: commit ──────────────────────────────────────────────────────────────────────────

  /** "Impor Sekarang". Defers one tick so the disabled "Mengimpor…" state can paint before the
   *  synchronous work runs, same trick `ImportWizard.js` already uses for its own (much larger)
   *  catalog commit. `groupApplyPreview`/`summary` are already the exact result of running
   *  `resolveContactImport` -> `resolveSalesPersonMapping` -> `commitContactImport` ->
   *  `applyConfirmedGroups` against the CURRENT state — nothing here recomputes that chain a
   *  second time; it just hands the already-computed `groupApplyPreview.data` (contacts AND
   *  confirmed businesses, in one object) up to `onCommit` (the caller's own single `update()`)
   *  and freezes the matching summary — both halves — as this screen's own RESULT view. */
  function runCommit() {
    setCommitting(true);
    setTimeout(() => {
      onCommit(groupApplyPreview.data);
      setFinalSummary({ ...summary, groups: groupApplyPreview });
      setCommitting(false);
    }, 0);
  }

  // ── Close ────────────────────────────────────────────────────────────────────────────────────
  const requestClose = () => {
    if (totalDecisionsCount > 0) setPendingClose(true);
    else onDone();
  };

  // ── Footer (manual-testing fix): Kembali/Lanjut pinned to the bottom on every step, plus a
  // live "what's still unanswered on THIS step" list — see the CSS's own header comment
  // (styles.css) for why `position: sticky` rather than `fixed` is what's used here. ────────────
  const goBack = () => setStep((s) => Math.max(1, s - 1));
  const goNext = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Impor Kontak</h1>
          <div className="page-sub">Langkah {step} dari {TOTAL_STEPS} — {STEP_TITLES[step - 1]}</div>
        </div>
        {!finalSummary && (
          <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
            <button type="button" className="btn" onClick={requestClose}>Tutup</button>
          </div>
        )}
      </div>

      {step === 1 && (
        <div className="card">
          <div className="form-row">
            <label className="form-label">Pilih File Excel (.xlsx)</label>
            <input type="file" accept=".xlsx" onChange={handleFileChange} />
          </div>

          {parseError && <div className="alert alert--error">{parseError}</div>}

          {parsed && (
            structuralErrors.length > 0 ? (
              <div className="alert alert--error">
                <div>File tidak bisa diproses:</div>
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {structuralErrors.map((p, i) => <li key={i}>{p.message}</li>)}
                </ul>
              </div>
            ) : (
              <>
                <div className="page-sub" style={{ marginBottom: "var(--gap-md)" }}>
                  CABANG: {cabangShops.length} toko · PUSAT: {pusatShops.length} toko
                </div>
                {warningCount > 0 && (
                  <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
                    {warningCount} hal perlu ditinjau di langkah berikutnya.
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}

      {step === 2 && parsed && (
        <div className="card">
          <div className="card__title">Nama Toko Ganda ({duplicateGroupDefs.length} grup)</div>
          {duplicateGroupDefs.length === 0 ? (
            <div className="form-hint">Tidak ada nama toko yang muncul lebih dari sekali.</div>
          ) : (
            duplicateGroupDefs.map((entry) => {
              const decision = normalizeDuplicateDecision(duplicateDecisions[entry.decisionKey]);
              const members = memberShops(entry);
              const comparisonRows = buildComparisonRows(members);
              const differing = differingComparisonFields(comparisonRows);
              const conflict = (unresolvedByType["duplicate-name-conflict"] || [])
                .find((u) => u.decisionKey === entry.decisionKey);
              return (
                <div key={entry.decisionKey} id={`group-${entry.decisionKey}`} className="card" style={{ marginBottom: "var(--gap-md)" }}>
                  <div><b>{entry.name}</b> — baris {entry.rows.join(", ")} ({entry.sheet})</div>

                  {/* Manual-testing fix: the user is deciding "one shop or two" and cannot do
                      that from a name + row list alone — every member row shown side by side,
                      whichever fields actually disagree highlighted (even an identical address
                      is not the same row: two different Sales names on it is a real case). */}
                  <div className="table-wrap" style={{ margin: "8px 0" }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Baris</th>
                          {COMPARISON_FIELDS.map((f) => <th key={f.key}>{f.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonRows.map((r) => (
                          <tr key={r.row} style={{ cursor: "default" }}>
                            <td>{r.row} ({entry.sheet})</td>
                            {COMPARISON_FIELDS.map((f) => (
                              <td key={f.key} style={differing.has(f.key) ? DIFFER_STYLE : undefined}>
                                {r[f.key]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="seg" style={{ margin: "8px 0" }}>
                    <button type="button" className={decision.kind === "merge" ? "active" : ""}
                      onClick={() => requestDuplicateKind(entry, "merge")}>Satu Toko</button>
                    <button type="button" className={decision.kind === "separate" ? "active" : ""}
                      onClick={() => requestDuplicateKind(entry, "separate")}>Toko Terpisah</button>
                  </div>
                  {!decision.kind && <div className="form-hint" style={{ color: "var(--amber)" }}>Belum diputuskan</div>}
                  {decision.kind === "separate" && (
                    <div>
                      {members.map((m) => {
                        const typed = (decision.names && decision.names[m.row]) || m.name;
                        const collision = renameCollision(entry.sheet, m.row, typed);
                        return (
                          <div key={m.row} className="form-row">
                            <label className="form-label">Baris {m.row} ({entry.sheet})</label>
                            <input className="form-input" value={typed}
                              onChange={(e) => setDuplicateRename(entry, m.row, e.target.value)} />
                            {collision && (
                              <div className="form-hint" style={{ color: "var(--red)" }}>
                                {collision.existing
                                  ? "Nama ini sudah dipakai kontak yang sudah ada."
                                  : `Nama ini sama dengan baris ${collision.withRow} (${entry.sheet}).`}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {conflict && <div className="form-hint" style={{ color: "var(--red)" }}>{conflict.message}</div>}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {step === 3 && parsed && (
        <div className="card">
          <div className="card__title">Alamat Tambahan ({(partialAfterDup.extraAddresses || []).length})</div>
          {(partialAfterDup.extraAddresses || []).length === 0 ? (
            <div className="form-hint">Tidak ada toko dengan lebih dari satu alamat.</div>
          ) : (
            partialAfterDup.extraAddresses.map((entry) => {
              const decision = normalizeAddressDecision(addressDecisions[entry.decisionKey]);
              return (
                <div key={entry.decisionKey} id={`extra-${entry.decisionKey}`} className="card" style={{ marginBottom: "var(--gap-md)" }}>
                  <div><b>{entry.name}</b> — baris {entry.row} ({entry.sheet}), {entry.addresses.length} alamat</div>
                  <div className="seg" style={{ margin: "8px 0" }}>
                    <button type="button" className={decision.action === "keepAll" ? "active" : ""}
                      onClick={() => setAddressAction(entry.decisionKey, "keepAll", [])}>Simpan Semua</button>
                    <button type="button" className={decision.action === "dedupe" ? "active" : ""}
                      onClick={() => setAddressAction(entry.decisionKey, "dedupe", decision.dropIndices)}>Hapus Duplikat</button>
                  </div>
                  <ul style={{ margin: "4px 0 0 18px" }}>
                    {entry.addresses.map((addr, idx) => (
                      <li key={idx}>
                        {decision.action === "dedupe" ? (
                          <label>
                            <input type="checkbox" disabled={idx === 0}
                              checked={decision.dropIndices.includes(idx)}
                              onChange={(e) => toggleDropIndex(entry, idx, e.target.checked)} />
                            {" "}{addr}
                            {idx === 0 && <span className="form-hint"> (alamat utama, tidak bisa dihapus)</span>}
                          </label>
                        ) : addr}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      )}

      {step === 4 && parsed && (
        <div>
          <div className="card" style={{ marginBottom: "var(--gap-md)" }}>
            <div className="card__title">Alamat Kosong ({(partialAfterDup.missingAddress || []).length})</div>
            {(partialAfterDup.missingAddress || []).length === 0 ? (
              <div className="form-hint">Tidak ada toko tanpa alamat.</div>
            ) : (
              <>
                {remainingAddressCount > 0 && (
                  <div style={{ marginBottom: "var(--gap-sm)" }}>
                    <button type="button" className="btn" onClick={leaveRemainingAddressesEmpty}>
                      Kosongkan Sisanya ({remainingAddressCount})
                    </button>
                    <div className="form-hint">
                      Mengisi kosong pada baris yang belum dijawab — tidak mengubah baris yang sudah diisi.
                    </div>
                  </div>
                )}
                {partialAfterDup.missingAddress.map((entry) => {
                  const decision = normalizeMissingAddressDecision(missingAddressDecisions[entry.decisionKey]);
                  const answered = Object.prototype.hasOwnProperty.call(missingAddressDecisions, entry.decisionKey);
                  const isEmpty = answered && decision.text === "";
                  return (
                    <div key={entry.decisionKey} id={`missing-addr-${entry.decisionKey}`} className="form-row">
                      <label className="form-label">{entry.name} — baris {entry.row} ({entry.sheet})</label>
                      <div style={{ display: "flex", gap: "var(--gap-sm)", alignItems: "center" }}>
                        <input className="form-input" value={decision.text} placeholder="Alamat" disabled={isEmpty}
                          onChange={(e) => setMissingAddressText(entry, e.target.value)} />
                        <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-xs)", whiteSpace: "nowrap", fontSize: 13 }}>
                          <input type="checkbox" checked={isEmpty}
                            onChange={(e) => (e.target.checked ? setMissingAddressEmpty(entry) : clearMissingAddressDecision(entry))} />
                          Biarkan kosong
                        </label>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <div className="card" style={{ marginBottom: "var(--gap-md)" }}>
            <div className="card__title">Kota Belum Diketahui ({(partialAfterDup.cityNotDerived || []).length})</div>
            {(partialAfterDup.cityNotDerived || []).length === 0 ? (
              <div className="form-hint">Tidak ada kota yang perlu diisi manual.</div>
            ) : (
              <>
                <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>
                  Kota tidak bisa dibaca dari alamat. Isi sendiri, atau biarkan kosong.
                </div>
                {remainingCityCount > 0 && (
                  <div style={{ marginBottom: "var(--gap-sm)" }}>
                    <button type="button" className="btn" onClick={leaveRemainingCitiesEmpty}>
                      Kosongkan Sisanya ({remainingCityCount})
                    </button>
                    <div className="form-hint">
                      Mengisi kosong pada baris yang belum dijawab — tidak mengubah baris yang sudah diisi.
                    </div>
                  </div>
                )}
                {partialAfterDup.cityNotDerived.map((entry) => {
                  const decision = normalizeCityDecision(cityDecisions[entry.decisionKey]);
                  const answered = Object.prototype.hasOwnProperty.call(cityDecisions, entry.decisionKey);
                  const isEmpty = answered && decision.city === "";
                  const problem = cityProblemByKey.get(entry.decisionKey);
                  return (
                    <div key={entry.decisionKey} id={`city-${entry.decisionKey}`} className="form-row">
                      <label className="form-label">{entry.name} — baris {entry.row} ({entry.sheet})</label>
                      {problem && problem.address && (
                        <div className="form-hint">Alamat: {problem.address}</div>
                      )}
                      <div style={{ display: "flex", gap: "var(--gap-sm)", alignItems: "center" }}>
                        <input className="form-input" value={decision.city} placeholder="Kota (boleh dikosongkan)" disabled={isEmpty}
                          onChange={(e) => setUnderivedCityText(entry, e.target.value)} />
                        <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-xs)", whiteSpace: "nowrap", fontSize: 13 }}>
                          <input type="checkbox" checked={isEmpty}
                            onChange={(e) => (e.target.checked ? setCityEmpty(entry) : clearCityDecision(entry))} />
                          Biarkan kosong
                        </label>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {derivedCityRows.length > 0 && (
            <div className="card" style={{ marginBottom: "var(--gap-md)" }}>
              <div className="card__title">Kota dari Alamat ({derivedCityRows.length}) — bisa diubah</div>
              <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>
                Diambil otomatis dari alamat. Periksa dan ubah jika salah.
              </div>
              {derivedCityRows.map((eff) => {
                const key = shopKey(eff.sheet, eff.anchorRow);
                const overridden = Object.prototype.hasOwnProperty.call(cityDecisions, key);
                const value = overridden ? normalizeCityDecision(cityDecisions[key]).city : eff.city.value;
                return (
                  <div key={key} className="form-row">
                    <label className="form-label">{eff.name} — baris {eff.anchorRow} ({eff.sheet})</label>
                    <input className="form-input" value={value}
                      onChange={(e) => setCityText(key, e.target.value)} />
                    {!overridden && <div className="form-hint">otomatis</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === 5 && parsed && (
        <div className="card">
          <div className="card__title">Sales Tambahan ({(partialAfterDup.secondSales || []).length})</div>
          {(partialAfterDup.secondSales || []).length === 0 ? (
            <div className="form-hint">Tidak ada baris dengan kemungkinan sales kedua.</div>
          ) : (
            <>
              {partialAfterDup.secondSales.map((entry) => {
                const decision = normalizeSecondSalesDecision(secondSalesDecisions[entry.decisionKey]);
                const members = [
                  {
                    row: entry.parentRow,
                    addresses: entry.parentAddress ? [entry.parentAddress] : [],
                    sales: entry.parentSales,
                    ekspedisi: entry.parentEkspedisi ? [entry.parentEkspedisi] : [],
                    contactPerson: entry.parentContactPerson ? [entry.parentContactPerson] : [],
                  },
                  {
                    row: entry.row,
                    addresses: entry.rowAddress ? [entry.rowAddress] : [],
                    sales: entry.rowSales,
                    ekspedisi: entry.rowEkspedisi ? [entry.rowEkspedisi] : [],
                    contactPerson: entry.rowContactPerson ? [entry.rowContactPerson] : [],
                  },
                ];
                const comparisonRows = buildComparisonRows(members);
                const differing = differingComparisonFields(comparisonRows);
                return (
                  <div key={entry.decisionKey} id={`second-sales-${entry.decisionKey}`} className="card" style={{ marginBottom: "var(--gap-md)" }}>
                    <div><b>{entry.name}</b> — baris {entry.parentRow} & {entry.row} ({entry.sheet})</div>

                    {/* Names THIS case's own rows and Sales values — not a description of the
                        general rule — reusing `contactImportResolve.js`'s own per-entry message
                        rather than a second, hand-written copy of the same sentence. */}
                    <div className="form-hint" style={{ margin: "6px 0" }}>{entry.message}</div>

                    {/* Same side-by-side comparison step 2's own duplicate-name review already
                        gives a human, reused verbatim — "why am I being asked" needs to be
                        answerable from the screen itself, not just a name + row number. */}
                    <div className="table-wrap" style={{ margin: "8px 0" }}>
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Baris</th>
                            {COMPARISON_FIELDS.map((f) => <th key={f.key}>{f.label}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonRows.map((r, i) => (
                            <tr key={r.row} style={{ cursor: "default" }}>
                              <td>{r.row} ({entry.sheet}) {i === 0 ? "— induk" : "— kelanjutan"}</td>
                              {COMPARISON_FIELDS.map((f) => (
                                <td key={f.key} style={differing.has(f.key) ? DIFFER_STYLE : undefined}>
                                  {r[f.key]}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="seg" style={{ margin: "8px 0" }}>
                      <button type="button" className={decision.action === "add" ? "active" : ""}
                        onClick={() => setSecondSalesAction(entry.decisionKey, "add")}>
                        Tambahkan sebagai Sales Kedua
                      </button>
                      <button type="button" className={decision.action === "keep" ? "active" : ""}
                        onClick={() => setSecondSalesAction(entry.decisionKey, "keep")}>
                        Pakai "{entry.parentSales}" Saja
                      </button>
                    </div>
                    {!decision.action && <div className="form-hint" style={{ color: "var(--amber)" }}>Belum diputuskan</div>}
                    {decision.action === "add" && (
                      <div className="form-hint">
                        Toko ini akan punya dua sales: "{entry.parentSales}" dan "{entry.rowSales}".
                      </div>
                    )}
                    {decision.action === "keep" && (
                      <div className="form-hint">Toko ini tetap hanya punya "{entry.parentSales}".</div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {step === 6 && parsed && (
        <div>
          <div className="card" style={{ marginBottom: "var(--gap-md)" }}>
            <div className="card__title">Pemetaan Sales ({regularSalesNames.length} nama)</div>
            {regularSalesNames.length === 0 ? (
              <div className="form-hint">Tidak ada nama sales yang perlu dipetakan.</div>
            ) : (
              regularSalesNames.map((entry) => {
                const decision = normalizeSalesMapDecision(salesMapDecisions[entry.key]);
                const collision = (salesMapResult.collisions || []).find((c) => c.key === entry.key);
                const rowIssue = (salesMapResult.unresolved || []).find((u) => u.key === entry.key);
                const collisionCheck = decision.action === "create" && decision.code
                  ? codeCollision(entry.key, decision.code)
                  : null;
                return (
                  <div key={entry.key} id={`sales-${entry.key}`} className="card" style={{ marginBottom: "var(--gap-md)" }}>
                    <div>
                      <b>{entry.name}</b> — dipakai {entry.shopCount} toko di {entry.sheets.join(" & ")}
                    </div>
                    <div className="seg" style={{ margin: "8px 0" }}>
                      <button type="button" className={decision.action === "map" ? "active" : ""}
                        onClick={() => setSalesMapAction(entry.key, "map", { personId: decision.personId || "" })}>
                        Pilih Orang
                      </button>
                      <button type="button" className={decision.action === "create" ? "active" : ""}
                        onClick={() => setSalesMapAction(entry.key, "create", {
                          name: decision.name || entry.name, code: decision.code || "",
                        })}>
                        Buat Orang Baru
                      </button>
                      <button type="button" className={decision.action === "skip" ? "active" : ""}
                        onClick={() => requestSkipSalesName(entry)}>
                        Lewati
                      </button>
                    </div>
                    {!decision.action && <div className="form-hint" style={{ color: "var(--amber)" }}>Belum diputuskan</div>}

                    {decision.action === "map" && (
                      <select className="form-input" value={decision.personId || ""}
                        onChange={(e) => setSalesMapAction(entry.key, "map", { personId: e.target.value })}>
                        <option value="">-- pilih orang --</option>
                        {(data.persons || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                        ))}
                      </select>
                    )}

                    {decision.action === "create" && (
                      <>
                        <div className="form-row">
                          <label className="form-label">Nama</label>
                          <input className="form-input" value={decision.name}
                            onChange={(e) => setSalesMapAction(entry.key, "create", { name: e.target.value, code: decision.code })} />
                        </div>
                        <div className="form-row">
                          <label className="form-label">Kode (1-4 huruf/angka, unik)</label>
                          <input className="form-input mono" style={{ width: 100 }} value={decision.code}
                            onChange={(e) => setSalesMapAction(entry.key, "create", { name: decision.name, code: e.target.value.toUpperCase() })} />
                          {decision.code && !codeFormatValid(decision.code) && (
                            <div className="form-hint" style={{ color: "var(--red)" }}>
                              Kode harus 1-4 huruf/angka (A-Z, 0-9).
                            </div>
                          )}
                          {decision.code && codeFormatValid(decision.code) && collisionCheck && (
                            <div className="form-hint" style={{ color: "var(--red)" }}>
                              {collisionCheck.type === "existing"
                                ? `Kode "${decision.code}" sudah dipakai orang lain di aplikasi.`
                                : `Kode "${decision.code}" sudah dipakai untuk "${collisionCheck.withName}" di langkah ini.`}
                            </div>
                          )}
                        </div>
                        {/* SAME-NAME PERSON: detection only, from resolveSalesPersonMapping's own
                            live `collisions` (never a second, re-derived check) — creating a
                            second person with the same name stays legal; this is one click away
                            from using the existing one instead, never forced. */}
                        {collision && collision.matches && collision.matches.length > 0 && (
                          <div className="form-hint" style={{ color: "var(--amber)" }}>
                            Orang bernama {collision.matches[0].name} sudah ada — pakai dia saja?
                            {collision.matches.map((m) => (
                              <button key={m.id} type="button" className="btn btn-sm" style={{ marginLeft: "var(--gap-sm)" }}
                                onClick={() => setSalesMapAction(entry.key, "map", { personId: m.id })}>
                                Pakai {m.name} ({m.code})
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {decision.action === "skip" && (() => {
                      const cost = skipCost(entry, resolved ? resolved.plan : []);
                      return cost > 0 ? (
                        <div className="form-hint" style={{ color: "var(--red)" }}>
                          {cost} toko tidak akan dibuat karena ini satu-satunya sales mereka.
                        </div>
                      ) : null;
                    })()}

                    {rowIssue && decision.action !== "create" && (
                      <div className="form-hint" style={{ color: "var(--red)" }}>{rowIssue.message}</div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Manual-testing fix — unusable Sales, moved here from step 4, resolved directly
              against people (never a Sales name first) — see this file's own header comment for
              the full data-contract note. */}
          {unusableSalesRows.entries.length > 0 && (
            <div className="card" style={{ marginBottom: "var(--gap-md)" }}>
              <div className="card__title">Toko dengan Sales Tidak Terbaca ({unusableSalesRows.entries.length})</div>
              <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>
                Sel Sales toko ini tidak bisa dibaca sama sekali — pilih orangnya langsung, tanpa perlu nama sales.
              </div>
              {unusableSalesRows.entries.map((e) => {
                const choice = unusablePersonChoices[e.decisionKey] || {};
                const problem = salesProblemByKey.get(e.decisionKey);
                const collision = choice.mode === "create"
                  ? (salesMapResult.collisions || []).find((c) => c.key === e.nameKey)
                  : null;
                const collisionCheck = choice.mode === "create" && choice.code
                  ? unusableCodeCollision(e.decisionKey, choice.code)
                  : null;
                const pickValue = choice.mode === "existing" ? `person:${choice.personId || ""}`
                  : choice.mode === "same" ? `name:${choice.nameKey || ""}`
                  : "";
                return (
                  <div key={e.decisionKey} id={`unusable-${e.decisionKey}`} className="card" style={{ marginBottom: "var(--gap-md)" }}>
                    <div>
                      <span className="chip chip--muted">{e.sheet}</span>{" "}
                      <b>{e.shopName}</b> — baris {e.row} ({e.sheet})
                    </div>
                    {problem && <div className="form-hint">{problem.message}</div>}
                    <div className="seg" style={{ margin: "8px 0" }}>
                      <button type="button" className={choice.mode === "existing" || choice.mode === "same" ? "active" : ""}
                        onClick={() => setUnusableChoice(e.decisionKey, { mode: "existing", personId: "" })}>
                        Pilih Orang
                      </button>
                      <button type="button" className={choice.mode === "create" ? "active" : ""}
                        onClick={() => setUnusableChoice(e.decisionKey, { mode: "create", name: "", code: "" })}>
                        Buat Orang Baru
                      </button>
                      <button type="button" className={choice.mode === "skip" ? "active" : ""}
                        onClick={() => setUnusableChoice(e.decisionKey, { mode: "skip" })}>
                        Lewati
                      </button>
                    </div>
                    {!choice.mode && <div className="form-hint" style={{ color: "var(--amber)" }}>Belum diputuskan</div>}

                    {(choice.mode === "existing" || choice.mode === "same") && (
                      <select className="form-input" value={pickValue}
                        onChange={(e2) => selectUnusablePerson(e.decisionKey, e2.target.value)}>
                        <option value="">-- pilih orang --</option>
                        {(data.persons || []).length > 0 && (
                          <optgroup label="Orang di aplikasi">
                            {data.persons.map((p) => (
                              <option key={p.id} value={`person:${p.id}`}>{p.name} ({p.code})</option>
                            ))}
                          </optgroup>
                        )}
                        {regularSalesNames.length > 0 && (
                          <optgroup label="Sales lain di langkah ini">
                            {regularSalesNames.map((n) => {
                              const outcome = regularNameOutcome(n);
                              return (
                                <option key={n.key} value={`name:${n.key}`} disabled={!outcome.pickable}>
                                  {outcome.label}
                                </option>
                              );
                            })}
                          </optgroup>
                        )}
                      </select>
                    )}

                    {choice.mode === "create" && (
                      <>
                        <div className="form-row">
                          <label className="form-label">Nama</label>
                          <input className="form-input" value={choice.name || ""}
                            onChange={(e2) => setUnusableChoice(e.decisionKey, { mode: "create", name: e2.target.value, code: choice.code || "" })} />
                        </div>
                        <div className="form-row">
                          <label className="form-label">Kode (1-4 huruf/angka, unik)</label>
                          <input className="form-input mono" style={{ width: 100 }} value={choice.code || ""}
                            onChange={(e2) => setUnusableChoice(e.decisionKey, { mode: "create", name: choice.name || "", code: e2.target.value.toUpperCase() })} />
                          {choice.code && !codeFormatValid(choice.code) && (
                            <div className="form-hint" style={{ color: "var(--red)" }}>
                              Kode harus 1-4 huruf/angka (A-Z, 0-9).
                            </div>
                          )}
                          {choice.code && codeFormatValid(choice.code) && collisionCheck && (
                            <div className="form-hint" style={{ color: "var(--red)" }}>
                              {collisionCheck.type === "existing"
                                ? `Kode "${choice.code}" sudah dipakai orang lain di aplikasi.`
                                : `Kode "${choice.code}" sudah dipakai untuk "${collisionCheck.withName}" di langkah ini.`}
                            </div>
                          )}
                        </div>
                        {collision && collision.matches && collision.matches.length > 0 && (
                          <div className="form-hint" style={{ color: "var(--amber)" }}>
                            Orang bernama {collision.matches[0].name} sudah ada — pakai dia saja?
                            {collision.matches.map((m) => (
                              <button key={m.id} type="button" className="btn btn-sm" style={{ marginLeft: "var(--gap-sm)" }}
                                onClick={() => setUnusableChoice(e.decisionKey, { mode: "existing", personId: m.id })}>
                                Pakai {m.name} ({m.code})
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {choice.mode === "skip" && (
                      <div className="form-hint" style={{ color: "var(--red)" }}>Toko ini tidak akan dibuat.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === 7 && parsed && (
        <div>
          {groupComputing && (
            <div className="card">
              <div className="form-hint">Mencari bisnis yang sama...</div>
            </div>
          )}

          {!groupComputing && groupingResult && (
            <div className="card">
              <div className="card__title">Kelompok Bisnis ({groupingResult.groups.length})</div>
              {groupingResult.groups.length === 0 ? (
                <div className="form-hint">Tidak ada kelompok bisnis yang diusulkan — tidak ada dua toko yang terlihat seperti bisnis yang sama.</div>
              ) : (
                <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
                  Diurutkan dari yang paling meyakinkan. Konfirmasi (dengan nama bisnis) atau tolak
                  masing-masing — membiarkan sebuah kelompok belum diputuskan sah saja, tidak ada
                  Bisnis yang dibuat untuknya.
                </div>
              )}

              {groupingResult.groups.map((g, gi) => {
                const decision = groupDecisions[gi] || {};
                const effective = effectiveGroupMembers(g, gi);
                const typedName = (decision.name || "").trim();
                const canConfirm = effective.length >= 2 && typedName.length > 0;
                return (
                  <div key={gi} id={`group-biz-${gi}`} className="card" style={{ marginBottom: "var(--gap-md)" }}>
                    <div>
                      <b>Kelompok {gi + 1}</b> — {effective.length} anggota
                      {!g.fullyConnected && (
                        <span className="chip" style={{ background: "#fef3c7", marginLeft: "var(--gap-sm)" }}>
                          terhubung berantai, tidak semua cocok langsung
                        </span>
                      )}
                    </div>
                    {!g.fullyConnected && (
                      <div className="form-hint">
                        Tidak setiap anggota cocok langsung dengan setiap anggota lain — sebagian
                        terhubung lewat anggota lain di kelompok ini. Periksa kecocokan di bawah
                        sebelum menggabungkan; keluarkan anggota yang tidak seharusnya ada di sini.
                      </div>
                    )}

                    {effective.map((m) => (
                      <div key={groupMemberKey(m.shop)} className="form-row" style={{ borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: "var(--gap-sm)" }}>
                        <div>
                          <span className="chip chip--muted">{m.shop.list}</span>{" "}
                          <b>{m.shop.name}</b>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: "var(--gap-sm)" }}
                            onClick={() => removeGroupMember(gi, m.shop)}>
                            Keluarkan
                          </button>
                        </div>
                        {m.matches.length > 0 ? (
                          <ul style={{ margin: "4px 0 0 18px" }}>
                            {m.matches.map((mm) => (
                              <li key={groupMemberKey(mm.shop)}>
                                Cocok dengan <b>{mm.shop.name}</b> ({mm.shop.list}) — {mm.reasons.join(", ")}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="form-hint">
                            Tidak cocok langsung dengan anggota lain — terhubung lewat rantai di atas.
                          </div>
                        )}
                      </div>
                    ))}

                    {effective.length < 2 && (
                      <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-sm)" }}>
                        Kurang dari dua anggota tersisa — tidak ada yang bisa dikonfirmasi.
                      </div>
                    )}

                    {effective.length >= 2 && (
                      <>
                        <div className="form-row" style={{ marginTop: "var(--gap-sm)" }}>
                          <label className="form-label">Nama Bisnis</label>
                          <input className="form-input" value={decision.name || ""} placeholder="Ketik nama bisnis"
                            onChange={(e) => setGroupName(gi, e.target.value)} />
                        </div>
                        <div className="seg" style={{ margin: "8px 0" }}>
                          <button type="button" className={decision.status === "confirmed" ? "active" : ""}
                            disabled={!canConfirm} onClick={() => setGroupStatus(gi, "confirmed")}>
                            Konfirmasi
                          </button>
                          <button type="button" className={decision.status === "rejected" ? "active" : ""}
                            onClick={() => setGroupStatus(gi, "rejected")}>
                            Tolak
                          </button>
                        </div>
                      </>
                    )}
                    {decision.status === "confirmed" && canConfirm && (
                      <div className="form-hint">Akan dibuat sebagai bisnis "{typedName}".</div>
                    )}
                    {decision.status === "rejected" && (
                      <div className="form-hint">Kelompok ini ditolak — tidak ada bisnis yang akan dibuat.</div>
                    )}
                    {!decision.status && (
                      <div className="form-hint" style={{ color: "var(--amber)" }}>Belum diputuskan.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === 8 && parsed && (
        <div className="card">
          <div className="card__title">{finalSummary ? "Hasil Impor" : "Ringkasan"}</div>

          {!finalSummary ? (
            <>
              <dl className="detail-grid">
                <dt>Kontak baru — CABANG</dt><dd><b>{commitPreview.report.perList.CABANG.createdCount}</b></dd>
                <dt>Kontak baru — PUSAT</dt><dd><b>{commitPreview.report.perList.PUSAT.createdCount}</b></dd>
                <dt>Orang baru</dt><dd><b>{summary.newPersons.length}</b></dd>
                <dt>Bisnis yang akan dibuat</dt><dd><b>{groupApplyPreview.createdBusinesses.length}</b></dd>
              </dl>

              {summary.newPersons.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <div className="card__title" style={{ fontSize: 14 }}>Orang baru yang akan dibuat</div>
                  <ul style={{ margin: "4px 0 0 18px" }}>
                    {summary.newPersons.map((p) => <li key={p.id}>{p.name} ({p.code})</li>)}
                  </ul>
                </div>
              )}

              {groupApplyPreview.createdBusinesses.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <div className="card__title" style={{ fontSize: 14 }}>
                    Bisnis yang akan dibuat ({groupApplyPreview.createdBusinesses.length})
                  </div>
                  <ul style={{ margin: "4px 0 0 18px" }}>
                    {groupApplyPreview.createdBusinesses.map((b) => (
                      <li key={b.groupIndex}>
                        <b>{b.name}</b> — {b.members.map((m) => `${m.name} (${m.list})`).join(", ")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {groupApplyPreview.undecidedGroups.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <details>
                    <summary className="card__title" style={{ fontSize: 14, cursor: "pointer" }}>
                      Kelompok bisnis belum diputuskan ({groupApplyPreview.undecidedGroups.length})
                    </summary>
                    <div className="form-hint" style={{ margin: "6px 0" }}>
                      Tidak ada Bisnis yang akan dibuat untuk kelompok ini — sah saja dibiarkan begitu.
                    </div>
                    <ul style={{ margin: "4px 0 0 18px", maxHeight: 260, overflowY: "auto" }}>
                      {groupApplyPreview.undecidedGroups.map((g) => (
                        <li key={g.groupIndex}>{g.memberNames.join(", ")}</li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}

              {groupApplyPreview.notLinked.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <div className="card__title" style={{ fontSize: 14 }}>
                    Toko tidak ditautkan ke bisnis ({groupApplyPreview.notLinked.length})
                  </div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead><tr><th>Sheet</th><th>Nama</th><th>Alasan</th></tr></thead>
                      <tbody>
                        {groupApplyPreview.notLinked.map((r, i) => (
                          <tr key={i} style={{ cursor: "default" }}>
                            <td>{r.list}</td><td>{r.name}</td><td>{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {summary.notCreated.length > 0 && (
                <div>
                  <div className="card__title" style={{ fontSize: 14 }}>
                    Toko yang TIDAK akan dibuat ({summary.notCreated.length})
                  </div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead><tr><th>Sheet</th><th>Nama</th><th>Alasan</th></tr></thead>
                      <tbody>
                        {summary.notCreated.map((r, i) => (
                          <tr key={i} style={{ cursor: "default" }}>
                            <td>{r.list}</td><td>{r.name}</td><td>{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <dl className="detail-grid">
                <dt>Kontak dibuat — CABANG</dt><dd><b>{finalSummary.report.perList.CABANG.createdCount}</b></dd>
                <dt>Kontak dibuat — PUSAT</dt><dd><b>{finalSummary.report.perList.PUSAT.createdCount}</b></dd>
                <dt>Kontak dilewati (sudah ada)</dt><dd><b>{finalSummary.report.skippedCount}</b></dd>
                <dt>Kontak gagal (validasi)</dt><dd><b>{finalSummary.report.failedCount}</b></dd>
                <dt>Orang baru dibuat</dt><dd><b>{finalSummary.newPersons.length}</b></dd>
                <dt>Bisnis dibuat</dt><dd><b>{finalSummary.groups.createdBusinesses.length}</b></dd>
              </dl>

              {finalSummary.newPersons.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <div className="card__title" style={{ fontSize: 14 }}>Orang baru yang dibuat</div>
                  <ul style={{ margin: "4px 0 0 18px" }}>
                    {finalSummary.newPersons.map((p) => <li key={p.id}>{p.name} ({p.code})</li>)}
                  </ul>
                </div>
              )}

              {finalSummary.groups.createdBusinesses.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <div className="card__title" style={{ fontSize: 14 }}>
                    Bisnis yang dibuat ({finalSummary.groups.createdBusinesses.length})
                  </div>
                  <ul style={{ margin: "4px 0 0 18px" }}>
                    {finalSummary.groups.createdBusinesses.map((b) => (
                      <li key={b.groupIndex}>
                        <b>{b.name}</b> — {b.members.map((m) => `${m.name} (${m.list})`).join(", ")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {finalSummary.groups.undecidedGroups.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <details>
                    <summary className="card__title" style={{ fontSize: 14, cursor: "pointer" }}>
                      Kelompok bisnis yang dibiarkan belum diputuskan ({finalSummary.groups.undecidedGroups.length})
                    </summary>
                    <ul style={{ margin: "4px 0 0 18px", maxHeight: 260, overflowY: "auto" }}>
                      {finalSummary.groups.undecidedGroups.map((g) => (
                        <li key={g.groupIndex}>{g.memberNames.join(", ")}</li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}

              {finalSummary.groups.notLinked.length > 0 && (
                <div style={{ marginBottom: "var(--gap-md)" }}>
                  <div className="card__title" style={{ fontSize: 14 }}>
                    Toko tidak ditautkan ke bisnis ({finalSummary.groups.notLinked.length})
                  </div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead><tr><th>Sheet</th><th>Nama</th><th>Alasan</th></tr></thead>
                      <tbody>
                        {finalSummary.groups.notLinked.map((r, i) => (
                          <tr key={i} style={{ cursor: "default" }}>
                            <td>{r.list}</td><td>{r.name}</td><td>{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {finalSummary.notCreated.length > 0 && (
                <div>
                  <div className="card__title" style={{ fontSize: 14 }}>
                    Toko yang TIDAK dibuat ({finalSummary.notCreated.length})
                  </div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead><tr><th>Sheet</th><th>Nama</th><th>Alasan</th></tr></thead>
                      <tbody>
                        {finalSummary.notCreated.map((r, i) => (
                          <tr key={i} style={{ cursor: "default" }}>
                            <td>{r.list}</td><td>{r.name}</td><td>{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Manual-testing fix — pinned footer, every step. */}
      <div className="wizard-footer">
        {footerItems.length > 0 && (
          <div className="wizard-footer__unresolved">
            {footerItems.map((item) => (
              <button key={item.key} type="button" className="wizard-footer__unresolved-item"
                onClick={() => jumpToRow(item.targetId)}>
                {item.label}
              </button>
            ))}
          </div>
        )}
        <div className="modal-actions">
          {step > 1 && step < 8 && (
            <button type="button" className="btn" onClick={goBack}>Kembali</button>
          )}
          {step === 8 && !finalSummary && (
            <button type="button" className="btn" onClick={goBack} disabled={committing}>Kembali</button>
          )}
          {step < 8 && (
            <button type="button" className="btn btn-primary" disabled={!footerCanAdvance} onClick={goNext}>Lanjut</button>
          )}
          {step === 8 && !finalSummary && (
            <button type="button" className="btn btn-primary"
              disabled={committing || !salesMapResult.readyToCommit}
              onClick={runCommit}>
              {committing ? "Mengimpor…" : "Impor Sekarang"}
            </button>
          )}
          {step === 8 && finalSummary && (
            <button type="button" className="btn btn-primary" onClick={onDone}>Kembali ke Kontak</button>
          )}
        </div>
      </div>

      {pendingDuplicateChange && (
        <ConfirmModal
          title="Ubah keputusan?"
          message={pendingDuplicateChange.message}
          confirmLabel="Lanjutkan"
          onConfirm={pendingDuplicateChange.onConfirm}
          onClose={() => setPendingDuplicateChange(null)}
        />
      )}

      {pendingClose && (
        <ConfirmModal
          title="Keluar dari impor?"
          message={`Keluar? ${totalDecisionsCount} keputusan akan hilang.`}
          confirmLabel="Keluar"
          onConfirm={onDone}
          onClose={() => setPendingClose(false)}
        />
      )}

      {pendingSkipSales && (
        <ConfirmModal
          title="Lewati sales ini?"
          message={pendingSkipSales.message}
          confirmLabel="Lewati"
          onConfirm={pendingSkipSales.onConfirm}
          onClose={() => setPendingSkipSales(null)}
        />
      )}
    </div>
  );
}
